import {
  type QuestionValue,
  type SubmissionFormData,
} from "@/components/DirectorySubmissions/Form/types";
import { type SupabaseClient } from "@supabase/supabase-js";
import { type EnqueueSnackbar } from "notistack";
import { useCallback, useRef, useState } from "react";

interface UseFieldSavingProps {
  supabase: SupabaseClient | undefined;
  firmId: string | undefined;
  userId: string | undefined;
  submissionId: string | undefined;
  formData: SubmissionFormData;
  initialFormData: SubmissionFormData | null;
  enqueueSnackbar: EnqueueSnackbar;
  externalSaveFunction?: (
    fieldId: string,
    updatedValue: QuestionValue,
  ) => Promise<void>;
}

interface CellUpdateMetadata {
  rowIndex: number;
  colIndex: number;
  cellValue: string | boolean | null;
}

/**
 * Hook for managing field saving with debouncing, versioning, and tracking pending saves
 * Now supports an optional externalSaveFunction for better integration with SubmissionsProvider
 * Validation is handled by saveData instead of in this hook
 */
export const useFieldSaving = ({
  supabase,
  firmId,
  userId,
  submissionId,
  formData,
  initialFormData,
  enqueueSnackbar,
  externalSaveFunction,
}: UseFieldSavingProps) => {
  const [pendingSaves, setPendingSaves] = useState<Set<string>>(new Set());
  const saveTimersRef = useRef<Record<string, NodeJS.Timeout>>({});
  const savingQueueRef = useRef<Map<string, any>>(new Map());
  const saveVersionRef = useRef<Record<string, number>>({});

  const saveField = useCallback(
    async (fieldId: string, value: any, onComplete?: () => void) => {
      if (!submissionId || !initialFormData) return;
      if (!supabase && !externalSaveFunction) return;

      // Check if this is a table field and if so, extract cell coordinates
      const isTableField =
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === "object" &&
        value[0] !== null &&
        "value" in value[0];

      let cellCoordinates: string | null = null;
      let originalValue = value;

      // For table fields, we need to track at the cell level
      if (isTableField) {
        // Check if this is a cell-specific update by looking for metadata
        const valueWithMeta = value as any;
        if (
          valueWithMeta._cellUpdate &&
          typeof valueWithMeta._cellUpdate === "object"
        ) {
          const { rowIndex, colIndex, cellValue } =
            valueWithMeta._cellUpdate as CellUpdateMetadata;

          // Create a unique identifier for this cell
          cellCoordinates = `${fieldId}:${rowIndex}:${colIndex}`;

          // Get the current form data for this field (the table)
          originalValue = [...((formData[fieldId] as any[]) || [])];

          // Ensure the row exists before trying to update it
          if (!originalValue[rowIndex]) {
            console.warn(`Row ${rowIndex} does not exist in table ${fieldId}`);
            // Don't create/modify rows during cell-level updates
            // This prevents unexpected row creation
            return;
          }

          // Clone the row's value array to avoid mutation issues
          const newRowValue = [...(originalValue[rowIndex].value || [])];

          // Update the specific cell
          newRowValue[colIndex] = cellValue;

          // Update the row with new values
          originalValue[rowIndex] = {
            ...originalValue[rowIndex],
            value: newRowValue,
          };

          // Remove the metadata before saving
          delete valueWithMeta._cellUpdate;
        }
      }

      // The unique identifier for this save operation - either cell coordinates or field ID
      const saveKey = cellCoordinates || fieldId;

      // Cancel any pending save for this field/cell
      if (saveTimersRef.current[saveKey]) {
        clearTimeout(saveTimersRef.current[saveKey]);
      }

      // Update version for this field/cell
      saveVersionRef.current[saveKey] =
        (saveVersionRef.current[saveKey] || 0) + 1;
      const currentVersion = saveVersionRef.current[saveKey];

      // Queue the change
      savingQueueRef.current.set(saveKey, originalValue);

      // Add field to pending saves
      setPendingSaves((prev) => {
        const newSet = new Set(prev);
        newSet.add(saveKey);
        return newSet;
      });

      // Debounce the save (500ms)
      saveTimersRef.current[saveKey] = setTimeout(() => {
        // Check if this save is still relevant
        if (currentVersion !== saveVersionRef.current[saveKey]) {
          return; // A newer save has been queued, skip this one
        }

        // Get the actual field ID (strip cell coordinates if present)
        const actualFieldId = cellCoordinates ? fieldId : saveKey;

        // Execute the save in the background
        void (async () => {
          try {
            await externalSaveFunction!(actualFieldId, value);

            // Remove from pending and queue only after successful save
            savingQueueRef.current.delete(saveKey);
            setPendingSaves((prev) => {
              const newSet = new Set(prev);
              newSet.delete(saveKey);
              return newSet;
            });

            onComplete?.();
          } catch (error) {
            console.error("Error saving field:", error);
            enqueueSnackbar("Failed to save changes", {
              variant: "error",
              anchorOrigin: { horizontal: "right", vertical: "bottom" },
            });

            // Clear pending status on error
            savingQueueRef.current.delete(saveKey);
            setPendingSaves((prev) => {
              const newSet = new Set(prev);
              newSet.delete(saveKey);
              return newSet;
            });
          }
        })();
      }, 500);
    },
    [
      submissionId,
      formData,
      initialFormData,
      supabase,
      firmId,
      userId,
      enqueueSnackbar,
      externalSaveFunction,
    ],
  );

  return {
    pendingSaves,
    saveField,
  };
};
